import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useHistory } from "react-router";
import useDesktopMediaQuery from "hooks/useDesktopMediaQuery";
import { useNotifications } from "utils/hooks";
import { app_pathnames, app_route_keys } from "constants/urlPaths";
import { useSessionData } from "../hooks/useSessionData";
import { useSessionParticipants } from "../../../hooks/useSessionParticipants";
import { useSessionBulkActions } from "../hooks/useSessionBulkActions";
import { useSessionModals } from "../hooks/useSessionModals";
import { useSessionExport } from "../hooks/useSessionExport";
import { useSessionParticipantsStore } from "../../../store/useSessionParticipantsStore";

const SessionParticipantsContext = createContext(null);

export const SessionParticipantsProvider = ({
  children,
  match,
  ...restProps
}) => {
  const history = useHistory();
  const { notify } = useNotifications();
  const isDesktop = useDesktopMediaQuery();

  const listingUuid = match?.params?.listing_uuid;
  const slotUuid = match?.params?.slotUuid;

  const [selectedIds, setSelectedIds] = useState([]);
  const storeFilterValues = useSessionParticipantsStore(
    (state) => state.filterValues
  );
  const setStoreFilterValues = useSessionParticipantsStore(
    (state) => state.setFilterValues
  );
  const [filterValuesState, setFilterValuesState] = useState({});

  const filterValues = useMemo(
    () => ({ ...storeFilterValues, ...filterValuesState }),
    [storeFilterValues, filterValuesState]
  );

  const setFilterValues = useCallback(
    (filters) => {
      setFilterValuesState(filters);
      setStoreFilterValues(filters);
    },
    [setStoreFilterValues]
  );

  const { sessionDetails, dataOfZoomAttendanceOfListing, fullListingData } =
    useSessionData({ listingUuid, slotUuid });

  const {
    listingName = "",
    listingType,
    coverImage,
    scheduleTypeLabel,
  } = dataOfZoomAttendanceOfListing;

  const { exportParticipants, refetch } = useSessionParticipants({
    slotUuid,
    filterValues,
  });

  const {
    showBroadcastModal,
    setShowBroadcastModal,
    broadcastActions,
    bulkActions,
    footerProps,
    selectedRecords,
    currentPageFullSelection,
    selectAll,
    handleSelectAll,
    total,
    onRowSelectedHandler,
  } = useSessionBulkActions({
    props: { match, ...restProps },
    listingUuid,
    slotUuid,
    filterValues,
    selectedIds,
    handleRowsSelected: setSelectedIds,
  });

  const modalStateAndHandlers = useSessionModals({ history });

  const { handleExport } = useSessionExport({
    exportParticipants,
    notify,
    selectedIds,
  });

  const handleBack = useCallback(() => {
    if (listingUuid) {
      history.push(
        app_pathnames[app_route_keys.zoom_attendance_of_listing]({
          listingUuid,
        })
      );
    } else {
      history.goBack();
    }
  }, [history, listingUuid]);

  const handleLearnMoreClick = useCallback(() => {
    modalStateAndHandlers.setShowLearnMorePopup(true);
  }, [modalStateAndHandlers]);

  const value = useMemo(
    () => ({
      listingUuid,
      slotUuid,
      selectedIds,
      setSelectedIds,
      filterValues,
      setFilterValues,
      sessionDetails,
      dataOfZoomAttendanceOfListing,
      fullListingData,
      listingName,
      listingType,
      coverImage,
      scheduleTypeLabel,
      exportParticipants,
      refetch,
      showBroadcastModal,
      setShowBroadcastModal,
      broadcastActions,
      bulkActions,
      footerProps,
      selectedRecords,
      currentPageFullSelection,
      selectAll,
      handleSelectAll,
      total,
      onRowSelectedHandler,
      ...modalStateAndHandlers,
      handleExport,
      handleBack,
      handleLearnMoreClick,
      isDesktop,
    }),
    [
      listingUuid,
      slotUuid,
      selectedIds,
      filterValues,
      setFilterValues,
      sessionDetails,
      dataOfZoomAttendanceOfListing,
      fullListingData,
      listingName,
      listingType,
      coverImage,
      scheduleTypeLabel,
      exportParticipants,
      refetch,
      showBroadcastModal,
      setShowBroadcastModal,
      broadcastActions,
      bulkActions,
      footerProps,
      selectedRecords,
      currentPageFullSelection,
      selectAll,
      handleSelectAll,
      total,
      onRowSelectedHandler,
      modalStateAndHandlers,
      handleExport,
      handleBack,
      handleLearnMoreClick,
      isDesktop,
    ]
  );

  return (
    <SessionParticipantsContext.Provider value={value}>
      {children}
    </SessionParticipantsContext.Provider>
  );
};

export const useSessionParticipantsContext = () => {
  const context = useContext(SessionParticipantsContext);
  return context || {};
};
